import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Modal } from '@/shared/ui/Modal'
import { Button } from '@/shared/ui/Button'
import { useConfirm } from '@/shared/ui/ConfirmProvider'
import { packageService } from '@/api/services/packageService'
import { restClient, getApiError } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import { cn } from '@/shared/utils'
import {
  Layers, ToggleRight, Percent, Building2, Languages, ListOrdered,
  MapPin, CalendarClock, UserCog, Plus, Trash2,
} from 'lucide-react'

interface Props {
  open: boolean
  onClose: () => void
  selectedIds: number[]
  onSuccess: () => void
}

// ── Small shared primitives ─────────────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400 bg-white'

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide block mb-1.5">
        {label}{hint && <span className="ml-1 normal-case text-gray-400 font-normal">({hint})</span>}
      </label>
      {children}
    </div>
  )
}

// Three-way switch — Unchanged / Yes / No — so a boolean field is only sent
// when the admin actually picked a value, instead of every toggle always
// going out with its default state and silently resetting it on every
// package in the batch (the bug in the previous version of this modal).
type Tri = '' | 'yes' | 'no'
function TriToggle({ label, value, onChange }: { label: string; value: Tri; onChange: (v: Tri) => void }) {
  const opts: { v: Tri; label: string }[] = [
    { v: '', label: 'Unchanged' }, { v: 'yes', label: 'Yes' }, { v: 'no', label: 'No' },
  ]
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-sm text-gray-700">{label}</span>
      <div className="flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5 flex-shrink-0">
        {opts.map(o => (
          <button key={o.v} type="button" onClick={() => onChange(o.v)}
            className={cn('px-2.5 py-1 rounded-md text-xs font-semibold transition-colors',
              value === o.v ? 'bg-brand-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700')}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function ToggleChip({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className={cn('px-2 py-1 rounded-lg text-xs font-bold flex-shrink-0', on ? 'bg-brand-500 text-white' : 'bg-gray-200 text-gray-600')}>
      {label}
    </button>
  )
}

function Switch({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick}
      className={cn('relative inline-flex w-10 h-5 rounded-full transition-colors flex-shrink-0', on ? 'bg-brand-500' : 'bg-gray-300')}>
      <span className={cn('absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform', on ? 'translate-x-5' : 'translate-x-0')} />
    </button>
  )
}

// A section that isn't a plain scalar field — schools, translations, session
// titles, city pricing, reschedule/trainer-change policy. Each carries its own
// "apply to selected packages" switch, collapsed by default: only sections the
// admin explicitly turns on end up in the payload, and only those show their
// (often long) editing UI, keeping the modal manageable at a glance.
function ApplySection({ icon, title, description, applied, onToggle, children }: {
  icon: React.ReactNode; title: string; description: string
  applied: boolean; onToggle: () => void; children: React.ReactNode
}) {
  return (
    <div className={cn('rounded-xl border transition-colors', applied ? 'border-brand-200 bg-brand-50/30' : 'border-gray-200 bg-white')}>
      <div className="flex items-center justify-between gap-3 px-4 py-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className={cn('w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0', applied ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-400')}>
            {icon}
          </span>
          <div className="min-w-0">
            <div className="text-sm font-bold text-gray-800">{title}</div>
            <div className="text-xs text-gray-400 truncate">{description}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs font-semibold text-gray-500">{applied ? 'Included' : 'Skip'}</span>
          <Switch on={applied} onClick={onToggle} />
        </div>
      </div>
      {applied && <div className="px-4 pb-4 pt-1 border-t border-brand-100">{children}</div>}
    </div>
  )
}

const COLORS = ['', 'unlimited', 'gold', 'silver', 'bronze', 'trial', 'express']
const LOCALES: { value: 'en' | 'ar' | 'ur'; label: string }[] = [
  { value: 'en', label: 'English' }, { value: 'ar', label: 'Arabic' }, { value: 'ur', label: 'Urdu' },
]

type CityRow = {
  cityId: string; minPrice: string; maxPrice: string
  commission: string; commissionType: 'percentage' | 'flat_amount'
  discountType: 'percentage' | 'amount'; discountValue: string; offerValidUntil: string
}
const emptyCityRow = (): CityRow => ({
  cityId: '', minPrice: '', maxPrice: '', commission: '', commissionType: 'percentage',
  discountType: 'percentage', discountValue: '', offerValidUntil: '',
})

const emptyScalars = {
  category_id: '', number_of_sessions: '', background_color: '',
  free_pickup_count: '', free_km: '',
  drivys_commision: '', is_drivys_commision_percentage: true,
  vendor_commision: '', certificate_fee: '',
}

export function PackageBulkEditModal({ open, onClose, selectedIds, onSuccess }: Props) {
  const confirm = useConfirm()
  const [form, setForm] = useState({ ...emptyScalars })
  const set = (k: keyof typeof emptyScalars, v: any) => setForm(f => ({ ...f, [k]: v }))

  // Status flags — tri-state so an untouched toggle is never sent (see TriToggle).
  const [isPublished, setIsPublished] = useState<Tri>('')
  const [isCertIncluded, setIsCertIncluded] = useState<Tri>('')
  const [isPickupIncluded, setIsPickupIncluded] = useState<Tri>('')
  const [isCashPay, setIsCashPay] = useState<Tri>('')

  // Schools / vendors
  const [applySchools, setApplySchools] = useState(false)
  const [schoolIds, setSchoolIds] = useState<number[]>([])

  // Translations
  const [applyTranslations, setApplyTranslations] = useState(false)
  const [translations, setTranslations] = useState<Record<'en' | 'ar' | 'ur', { name: string; incl: string }>>({
    en: { name: '', incl: '' }, ar: { name: '', incl: '' }, ur: { name: '', incl: '' },
  })

  // Session titles
  const [applySessionTitles, setApplySessionTitles] = useState(false)
  const [titleLocale, setTitleLocale] = useState<'en' | 'ar' | 'ur'>('en')
  const [sessionTitles, setSessionTitles] = useState<Record<'en' | 'ar' | 'ur', string[]>>({ en: [''], ar: [''], ur: [''] })

  // City pricing
  const [applyCities, setApplyCities] = useState(false)
  const [cityRows, setCityRows] = useState<CityRow[]>([emptyCityRow()])

  // Reschedule policy
  const [applyReschedule, setApplyReschedule] = useState(false)
  const [rescheduleEnabled, setRescheduleEnabled] = useState(true)
  const [freeReschedule, setFreeReschedule] = useState('')
  const [rescheduleHours, setRescheduleHours] = useState('24')

  // Trainer change policy
  const [applyTrainerChange, setApplyTrainerChange] = useState(false)
  const [trainerChangeEnabled, setTrainerChangeEnabled] = useState(true)
  const [freeTrainerChange, setFreeTrainerChange] = useState('')

  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)

  const { data: catData } = useQuery({
    queryKey: ['categories-for-bulk'],
    queryFn: () => restClient.get(`${endpoints.users.listCategory}?page=1&limit=1000`).then(r => r.data),
    enabled: open, staleTime: 60_000,
  })
  const categories: any[] = (catData as any)?.data ?? []
  const catName = (c: any) => c?.category_translations?.find((t: any) => t?.locale?.toLowerCase() === 'en')?.name ?? c?.category_translations?.[0]?.name ?? `#${c.id}`

  const { data: schoolData } = useQuery({
    queryKey: ['schools-for-bulk'],
    queryFn: () => restClient.get(`${endpoints.school.list}?page=1&limit=1000`).then(r => r.data),
    enabled: open, staleTime: 60_000,
  })
  const schools: any[] = (schoolData as any)?.data ?? []
  const schoolName = (s: any) => s?.vendor_translations?.find((t: any) => t?.locale?.toLowerCase() === 'en')?.name ?? s?.vendor_translations?.[0]?.name ?? `#${s.id}`

  const { data: cityData } = useQuery({
    queryKey: ['cities-for-bulk'],
    queryFn: () => restClient.get(`${endpoints.city.list}?limit=1000&locale=en`).then(r => r.data),
    enabled: open, staleTime: 60_000,
  })
  const cities: any[] = (cityData as any)?.data ?? []
  const cityName = (c: any) => c?.city_translations?.find((t: any) => t?.locale?.toLowerCase() === 'en')?.name ?? c?.city_translations?.[0]?.name ?? `#${c.id}`

  const toggleSchool = (id: number) => setSchoolIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const setCityRow = (i: number, k: keyof CityRow, v: string) => setCityRows(rows => rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r))
  const addCityRow = () => setCityRows(rows => [...rows, emptyCityRow()])
  const removeCityRow = (i: number) => setCityRows(rows => rows.length > 1 ? rows.filter((_, idx) => idx !== i) : rows)
  const setTitleAt = (loc: 'en' | 'ar' | 'ur', i: number, v: string) =>
    setSessionTitles(s => ({ ...s, [loc]: s[loc].map((t, idx) => idx === i ? v : t) }))
  const addTitle = (loc: 'en' | 'ar' | 'ur') => setSessionTitles(s => ({ ...s, [loc]: [...s[loc], ''] }))
  const removeTitle = (loc: 'en' | 'ar' | 'ur', i: number) =>
    setSessionTitles(s => ({ ...s, [loc]: s[loc].length > 1 ? s[loc].filter((_, idx) => idx !== i) : s[loc] }))

  const changedCount = [
    form.category_id, form.number_of_sessions, form.background_color, form.free_pickup_count, form.free_km,
    form.drivys_commision, form.vendor_commision, form.certificate_fee,
  ].filter(v => v !== '').length
    + [isPublished, isCertIncluded, isPickupIncluded, isCashPay].filter(Boolean).length
    + [applySchools, applyTranslations, applySessionTitles, applyCities, applyReschedule, applyTrainerChange].filter(Boolean).length

  const resetAll = () => {
    setForm({ ...emptyScalars })
    setIsPublished(''); setIsCertIncluded(''); setIsPickupIncluded(''); setIsCashPay('')
    setApplySchools(false); setSchoolIds([])
    setApplyTranslations(false); setTranslations({ en: { name: '', incl: '' }, ar: { name: '', incl: '' }, ur: { name: '', incl: '' } })
    setApplySessionTitles(false); setTitleLocale('en'); setSessionTitles({ en: [''], ar: [''], ur: [''] })
    setApplyCities(false); setCityRows([emptyCityRow()])
    setApplyReschedule(false); setRescheduleEnabled(true); setFreeReschedule(''); setRescheduleHours('24')
    setApplyTrainerChange(false); setTrainerChangeEnabled(true); setFreeTrainerChange('')
    setError('')
  }
  const handleClose = () => { resetAll(); onClose() }

  const handleSave = async () => {
    setError('')
    if (form.number_of_sessions !== '' && Number(form.number_of_sessions) !== -1 && Number(form.number_of_sessions) % 2 !== 0) {
      setError("Number of sessions must be an even number (or -1 for unlimited).")
      return
    }
    if (form.is_drivys_commision_percentage && form.drivys_commision !== '' && Number(form.drivys_commision) > 100) {
      setError("Drivy's Commission should be ≤ 100% when in percentage.")
      return
    }
    if (applySchools && schoolIds.length === 0) { setError('Select at least one school, or turn off School Assignment.'); return }
    if (applyCities && cityRows.some(r => !r.cityId)) { setError('Every city pricing row needs a city selected.'); return }

    if (!(await confirm({
      title: 'Update these packages?',
      description: `This will apply ${changedCount} field${changedCount === 1 ? '' : 's'}/section${changedCount === 1 ? '' : 's'} of changes to all ${selectedIds.length} selected package${selectedIds.length === 1 ? '' : 's'}. This can't be undone from here.`,
      confirmLabel: `Update ${selectedIds.length} Package${selectedIds.length === 1 ? '' : 's'}`,
      variant: 'primary',
    }))) return

    setSaving(true)
    try {
      const payload: Record<string, unknown> = { package_ids: selectedIds }

      if (form.category_id) payload.category_id = Number(form.category_id)
      if (form.number_of_sessions !== '') payload.number_of_sessions = Number(form.number_of_sessions)
      if (form.background_color) payload.background_color = form.background_color
      if (form.free_pickup_count !== '') payload.free_pickup_count = Number(form.free_pickup_count)
      if (form.free_km !== '') payload.free_km = Number(form.free_km)
      if (form.drivys_commision !== '') {
        payload.drivys_commision = Number(form.drivys_commision)
        payload.is_drivys_commision_percentage = form.is_drivys_commision_percentage
      }
      if (form.vendor_commision !== '') payload.vendor_commision = Number(form.vendor_commision)
      if (form.certificate_fee !== '') payload.certificate_fee = Number(form.certificate_fee)

      if (isPublished) payload.is_published = isPublished === 'yes'
      if (isCertIncluded) payload.is_certificate_included = isCertIncluded === 'yes'
      if (isPickupIncluded) payload.is_pickup_fee_included = isPickupIncluded === 'yes'
      if (isCashPay) payload.is_cash_pay_available = isCashPay === 'yes'

      if (applySchools) payload.vendor_id = schoolIds

      if (applyTranslations) {
        const rows = (['en', 'ar', 'ur'] as const)
          .filter(loc => translations[loc].name.trim() || translations[loc].incl.trim())
          .map(loc => ({ locale: loc, name: translations[loc].name.trim(), session_inclusions: translations[loc].incl.trim() }))
        if (rows.length) payload.package_translation = rows
      }

      if (applySessionTitles) {
        const rows = (['en', 'ar', 'ur'] as const)
          .map(loc => ({ locale: loc, titles: sessionTitles[loc].map(t => t.trim()).filter(Boolean) }))
          .filter(r => r.titles.length)
        if (rows.length) payload.session_titles = rows
      }

      if (applyCities) {
        payload.cities_ids = cityRows.filter(r => r.cityId).map(r => ({
          id: Number(r.cityId),
          min_price: Number(r.minPrice || 0),
          max_price: Number(r.maxPrice || 0),
          commision: Number(r.commission || 0),
          commision_type: r.commissionType,
          discount_type: r.discountType,
          discount_value: Number(r.discountValue || 0),
          ...(r.offerValidUntil ? { offer_valid_until: r.offerValidUntil } : {}),
        }))
      }

      if (applyReschedule) {
        payload.reschedule_policy = rescheduleEnabled
          ? { enabled: true, free_reschedule_count: Number(freeReschedule || 0), reschedule_hours_before: Number(rescheduleHours || 24) }
          : { enabled: false, free_reschedule_count: 0, reschedule_hours_before: 0 }
      }

      if (applyTrainerChange) {
        payload.trainer_change_policy = trainerChangeEnabled
          ? { enabled: true, free_trainer_change_count: Number(freeTrainerChange || 0) }
          : { enabled: false, free_trainer_change_count: 0 }
      }

      await packageService.bulkEdit(payload)
      onSuccess()
      handleClose()
    } catch (e) {
      setError(getApiError(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal open={open} onClose={handleClose} title={`Edit Multiple Packages (${selectedIds.length} selected)`} size="xl">
      <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1">
        <div className="bg-brand-50 border border-brand-200 rounded-lg px-3 py-2 text-xs text-brand-700">
          Fields left blank, toggles left on "Unchanged", and sections left off are not touched — only what you fill in or switch on below gets applied to all {selectedIds.length} selected packages.
        </div>
        {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-xs text-red-600">{error}</div>}

        {/* ── Basic details ── */}
        <div className="rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Layers size={15} className="text-gray-500" />
            <span className="text-sm font-bold text-gray-800">Basic Details</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category_id} onChange={e => set('category_id', e.target.value)} className={inputCls}>
                <option value="">— No change —</option>
                {categories.map(c => <option key={c.id} value={c.id}>{catName(c)}</option>)}
              </select>
            </Field>
            <Field label="Number of Sessions" hint="-1 = unlimited, otherwise even">
              <input type="number" value={form.number_of_sessions} onChange={e => set('number_of_sessions', e.target.value)} placeholder="Leave empty to keep" className={inputCls} />
            </Field>
            <Field label="Background Color">
              <select value={form.background_color} onChange={e => set('background_color', e.target.value)} className={cn(inputCls, 'capitalize')}>
                {COLORS.map(c => <option key={c} value={c}>{c === '' ? '— No change —' : c}</option>)}
              </select>
            </Field>
            <Field label="Free Pickup Count">
              <input type="number" min="0" value={form.free_pickup_count} onChange={e => set('free_pickup_count', e.target.value)} placeholder="Leave empty to keep" className={inputCls} />
            </Field>
            <Field label="Free KM">
              <input type="number" min="0" value={form.free_km} onChange={e => set('free_km', e.target.value)} placeholder="Leave empty to keep" className={inputCls} />
            </Field>
          </div>
        </div>

        {/* ── Status flags ── */}
        <div className="rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-1">
            <ToggleRight size={15} className="text-gray-500" />
            <span className="text-sm font-bold text-gray-800">Status & Payment</span>
          </div>
          <div className="divide-y divide-gray-50">
            <TriToggle label="Published" value={isPublished} onChange={setIsPublished} />
            <TriToggle label="Certificate Included" value={isCertIncluded} onChange={setIsCertIncluded} />
            <TriToggle label="Pickup Fee Included" value={isPickupIncluded} onChange={setIsPickupIncluded} />
            <TriToggle label="Cash Payment Available" value={isCashPay} onChange={setIsCashPay} />
          </div>
        </div>

        {/* ── Commission & fees ── */}
        <div className="rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 mb-3">
            <Percent size={15} className="text-gray-500" />
            <span className="text-sm font-bold text-gray-800">Commission & Fees</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Drivy's Commission">
              <div className="flex items-center gap-2">
                <input type="number" value={form.drivys_commision} onChange={e => set('drivys_commision', e.target.value)} placeholder="Leave empty to keep" className={inputCls} />
                <ToggleChip on={form.is_drivys_commision_percentage} onClick={() => set('is_drivys_commision_percentage', !form.is_drivys_commision_percentage)} label={form.is_drivys_commision_percentage ? '%' : 'AED'} />
              </div>
            </Field>
            <Field label="Vendor Commission">
              <input type="number" value={form.vendor_commision} onChange={e => set('vendor_commision', e.target.value)} placeholder="Leave empty to keep" className={inputCls} />
            </Field>
            <Field label="Certificate Fee">
              <input type="number" value={form.certificate_fee} onChange={e => set('certificate_fee', e.target.value)} placeholder="Leave empty to keep" className={inputCls} />
            </Field>
          </div>
        </div>

        {/* ── School / vendor assignment ── */}
        <ApplySection icon={<Building2 size={15} />} title="School Assignment"
          description="Assign these packages to specific schools" applied={applySchools} onToggle={() => setApplySchools(v => !v)}>
          <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto pt-2">
            {schools.map(s => (
              <button key={s.id} type="button" onClick={() => toggleSchool(s.id)}
                className={cn('px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors',
                  schoolIds.includes(s.id) ? 'bg-brand-500 text-white border-brand-500' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300')}>
                {schoolName(s)}
              </button>
            ))}
            {schools.length === 0 && <p className="text-xs text-gray-400">No schools found.</p>}
          </div>
        </ApplySection>

        {/* ── Translations ── */}
        <ApplySection icon={<Languages size={15} />} title="Translations"
          description="Package name & session inclusions per language" applied={applyTranslations} onToggle={() => setApplyTranslations(v => !v)}>
          <div className="space-y-3 pt-2">
            {LOCALES.map(({ value, label }) => (
              <div key={value} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label={`Name (${label})`}>
                  <input value={translations[value].name} onChange={e => setTranslations(t => ({ ...t, [value]: { ...t[value], name: e.target.value } }))}
                    placeholder="Leave empty to keep" className={inputCls} />
                </Field>
                <Field label={`Session Inclusions (${label})`}>
                  <input value={translations[value].incl} onChange={e => setTranslations(t => ({ ...t, [value]: { ...t[value], incl: e.target.value } }))}
                    placeholder="Leave empty to keep" className={inputCls} />
                </Field>
              </div>
            ))}
          </div>
        </ApplySection>

        {/* ── Session titles ── */}
        <ApplySection icon={<ListOrdered size={15} />} title="Session Titles"
          description="Ordered list of session names, per language" applied={applySessionTitles} onToggle={() => setApplySessionTitles(v => !v)}>
          <div className="pt-2 space-y-3">
            <div className="flex items-center gap-1.5">
              {LOCALES.map(l => (
                <button key={l.value} type="button" onClick={() => setTitleLocale(l.value)}
                  className={cn('px-2.5 py-1 rounded-lg text-xs font-semibold',
                    titleLocale === l.value ? 'bg-brand-500 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200')}>
                  {l.label}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              {sessionTitles[titleLocale].map((title, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-400 w-5 flex-shrink-0">{i + 1}.</span>
                  <input value={title} onChange={e => setTitleAt(titleLocale, i, e.target.value)}
                    placeholder={`Session ${i + 1} title`} className={inputCls} />
                  <button type="button" onClick={() => removeTitle(titleLocale, i)}
                    className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button type="button" onClick={() => addTitle(titleLocale)}
                className="flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700">
                <Plus size={13} /> Add title
              </button>
            </div>
          </div>
        </ApplySection>

        {/* ── City pricing & discounts ── */}
        <ApplySection icon={<MapPin size={15} />} title="City Pricing & Discounts"
          description="Per-city price, commission and offer" applied={applyCities} onToggle={() => setApplyCities(v => !v)}>
          <div className="pt-2 space-y-3">
            {cityRows.map((row, i) => (
              <div key={i} className="rounded-lg border border-gray-100 bg-gray-50/60 p-3 space-y-2.5">
                <div className="flex items-center gap-2">
                  <select value={row.cityId} onChange={e => setCityRow(i, 'cityId', e.target.value)} className={cn(inputCls, 'flex-1')}>
                    <option value="">Select city…</option>
                    {cities.map(c => <option key={c.id} value={c.id}>{cityName(c)}</option>)}
                  </select>
                  <button type="button" onClick={() => removeCityRow(i)}
                    className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 flex-shrink-0">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Field label="Min Price"><input type="number" value={row.minPrice} onChange={e => setCityRow(i, 'minPrice', e.target.value)} className={inputCls} /></Field>
                  <Field label="Max Price"><input type="number" value={row.maxPrice} onChange={e => setCityRow(i, 'maxPrice', e.target.value)} className={inputCls} /></Field>
                  <Field label="Commission"><input type="number" value={row.commission} onChange={e => setCityRow(i, 'commission', e.target.value)} className={inputCls} /></Field>
                  <Field label="Commission Type">
                    <select value={row.commissionType} onChange={e => setCityRow(i, 'commissionType', e.target.value)} className={inputCls}>
                      <option value="percentage">Percentage</option><option value="flat_amount">Flat Amount</option>
                    </select>
                  </Field>
                  <Field label="Discount Type">
                    <select value={row.discountType} onChange={e => setCityRow(i, 'discountType', e.target.value)} className={inputCls}>
                      <option value="percentage">Percentage</option><option value="amount">Amount</option>
                    </select>
                  </Field>
                  <Field label="Discount Value"><input type="number" value={row.discountValue} onChange={e => setCityRow(i, 'discountValue', e.target.value)} className={inputCls} /></Field>
                  <div className="col-span-2">
                    <Field label="Offer Valid Until"><input type="date" value={row.offerValidUntil} onChange={e => setCityRow(i, 'offerValidUntil', e.target.value)} className={inputCls} /></Field>
                  </div>
                </div>
              </div>
            ))}
            <button type="button" onClick={addCityRow}
              className="flex items-center gap-1 text-xs font-semibold text-brand-600 hover:text-brand-700">
              <Plus size={13} /> Add another city
            </button>
          </div>
        </ApplySection>

        {/* ── Reschedule policy ── */}
        <ApplySection icon={<CalendarClock size={15} />} title="Reschedule Policy"
          description="Free reschedules and the minimum notice required" applied={applyReschedule} onToggle={() => setApplyReschedule(v => !v)}>
          <div className="pt-2 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Allow rescheduling</span>
              <Switch on={rescheduleEnabled} onClick={() => setRescheduleEnabled(v => !v)} />
            </div>
            {rescheduleEnabled && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Free Reschedule Count"><input type="number" min="0" value={freeReschedule} onChange={e => setFreeReschedule(e.target.value)} className={inputCls} /></Field>
                <Field label="Hours Notice Required"><input type="number" min="0" value={rescheduleHours} onChange={e => setRescheduleHours(e.target.value)} className={inputCls} /></Field>
              </div>
            )}
          </div>
        </ApplySection>

        {/* ── Trainer change policy ── */}
        <ApplySection icon={<UserCog size={15} />} title="Trainer Change Policy"
          description="Whether students can request a different trainer" applied={applyTrainerChange} onToggle={() => setApplyTrainerChange(v => !v)}>
          <div className="pt-2 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Allow trainer changes</span>
              <Switch on={trainerChangeEnabled} onClick={() => setTrainerChangeEnabled(v => !v)} />
            </div>
            {trainerChangeEnabled && (
              <Field label="Free Trainer Change Count"><input type="number" min="0" value={freeTrainerChange} onChange={e => setFreeTrainerChange(e.target.value)} className={inputCls} /></Field>
            )}
          </div>
        </ApplySection>

        <div className="flex items-center justify-between gap-2 pt-2 sticky bottom-0 bg-white">
          <span className="text-xs text-gray-400">
            {changedCount > 0 ? `${changedCount} field${changedCount === 1 ? '' : 's'}/section${changedCount === 1 ? '' : 's'} will be updated` : 'Nothing selected to change yet'}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClose}>Cancel</Button>
            <Button loading={saving} disabled={changedCount === 0} onClick={handleSave}>Update {selectedIds.length} Packages</Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}
